/**
 * Browser-compatible test harness — mirrors the Node.js `createTestHarness()`
 * from `./harness.ts` but uses only `@gotti/core` imports (no `@gotti/server`).
 *
 * This is the package's `@gotti/test-utils/harness-browser` entry, which a page
 * (the sandbox runner) imports by that subpath. The package root's
 * `createTestHarness` is `./harness.ts` (which uses the real `ServerProcess`).
 *
 * All routing, lifecycle, and convenience logic lives in `./harness-core.ts` —
 * this file only provides the browser-specific Process subclasses.
 */
import { Process, System } from '@gotti/core';
import { CLIENT_SYSTEM_PROTO } from '@gotti/client';
import {
  buildTestHarness,
  createAreaServerStub,
  setupSystemPair as _setupSystemPair,
} from './harness-core.js';
import { installFocusScopedInput } from './focus-scoped-input.js';

// Re-export shared types so consumers get everything from one import
export type {
  TestHarness,
  ServerToClientRouter,
  ServerToClientRequester,
  ClientToServerRequester,
} from './harness-core.js';

// Re-export browser-safe canvas assertion helpers
export {
  getPixelStats,
  assertCanvasNotBlank,
  getColorPresence,
  assertColorPresent,
  detectLines,
  detectGrid,
  createTestCanvas,
} from './canvas-assertions.js';
export type {
  PixelStats,
  ColorPresence,
  LineSegment,
  LineDetectionResult,
  GridDetectionResult,
} from './canvas-assertions.js';

import type { TestHarness } from './harness-core.js';

// ── ServerProcess-equivalent for browser ─────────────────────────────────────
// Replicates the essential ServerProcess behavior using only @gotti/core:
// 1. decorate() — adds __is_server, requestCbs, meta to server systems
// 2. onAddedSystem() — binds dispatch methods from the area server stub
// 3. initialize() — stores the area server stub and binds methods on this

const SERVER_DISPATCH_METHODS = [
  'dispatchToAllClients', 'dispatchToClients', 'dispatchToClient',
  'dispatchToAreas', 'dispatchToArea', 'dispatchRaw',
  'requestArea', 'requestClient', 'requestMaster',
  'addClientToArea', 'replaceClientArea', 'removeClientFromArea',
  'dispatchToMaster', 'createArea', 'destroyArea', 'setState',
  'createChannel', 'destroyChannel', 'addClientToChannel',
  'removeClientFromChannel', 'dispatchToChannel', 'requestChannel',
  'getClientsInChannel',
] as const;

class BrowserServerProcess extends Process {
  private areaServer: any;

  public initialize(server: any): void {
    this.areaServer = server;
    for (const name of SERVER_DISPATCH_METHODS) {
      if (typeof server[name] === 'function') {
        (this as any)[name] = server[name].bind(server);
      }
    }
  }

  public decorate(system: System<any>): System<any> {
    if (!('__is_server' in system)) {
      Object.defineProperty(system, '__is_server', {
        value: true, writable: false, configurable: false, enumerable: false,
      });
    }
    if (!('requestCbs' in system)) {
      Object.defineProperty(system, 'requestCbs', {
        value: {}, writable: true, configurable: true, enumerable: true,
      });
    }
    if (!('meta' in system)) {
      Object.defineProperty(system, 'meta', {
        value: { areaId: '', areaType: '', gameId: '', gameType: '', areaConfig: null, gameConfig: null },
        writable: true, configurable: true, enumerable: true,
      });
    }
    return system;
  }

  public onAddedSystem(system: System<any>): void {
    if (this.areaServer) {
      for (const name of SERVER_DISPATCH_METHODS) {
        if (typeof this.areaServer[name] === 'function') {
          (system as any)[name] = this.areaServer[name].bind(this.areaServer);
        }
      }
    }
  }

  // The real ServerProcess flips `this.started` inside its onStartLoop —
  // we mirror that so post-start `addSystem()` calls fire `onStart` on the
  // new system (Process._addSystem guards onStart with `if (this.started)`).
  protected onStartLoop(): void { this.started = true; }
  protected onStopLoop(): void { this.started = false; }
}

// ── Client Process ───────────────────────────────────────────────────────────
//
// Mirrors the production ClientProcess (packages/client/src/ClientProcess.ts)
// closely enough for in-process tests:
//   - decorate()       — copies method bodies from CLIENT_SYSTEM_PROTO so
//                        `this.dispatchToServer`, `this.requestArea`, etc.
//                        actually exist on every system instance.
//   - onAddedSystem()  — installs `$areaData` and calls `system.initialize(
//                        client, connector)` so the methods above can run
//                        (they all dispatch through `this.connector.*`).
//   - onStartLoop()    — flips `this.started = true` so systems added AFTER
//                        the loop starts also receive their `onStart()` call
//                        (matches ClientProcess.ts:249 behaviour).
//
// Historical note: this class was previously a 6-line stub with `decorate`
// and `onAddedSystem` as no-ops, which meant client systems added through
// `harness.addClientSystem(...)` ran without their dispatch methods OR their
// connector wired up. Tests that exercised client→server dispatch had to
// hand-patch `client.connector` themselves (see __template/integration/
// TemplateSystem.integration.test.ts:27-29 for the workaround). Closing
// that gap so test instances look like production instances.

const TEST_CLIENT_CONNECTOR_METHODS = ['send', 'sendRaw', 'requestServer'] as const;
type TestClientConnector = { [K in (typeof TEST_CLIENT_CONNECTOR_METHODS)[number]]: (...args: any[]) => any };

// Single shared area-data snapshot. Key matches `createAreaServerStub`'s
// 'test-area-1' so `proto.requestArea(...)` auto-resolves `areaId` when the
// caller omits it. Frozen because every client system reads it through the
// same getter and a mutation would leak across tests.
const DEFAULT_TEST_AREA_DATA: Readonly<Record<string, unknown>> = Object.freeze({
  'test-area-1': Object.freeze({}),
});

export class TestProcess extends Process {
  private _client: any = {};
  private _connector: TestClientConnector | null = null;

  public initialize(connector: TestClientConnector): void {
    this._connector = connector;
  }

  public decorate(system: System<any>): System<any> {
    if ((system as any).__is_client) return system;
    // Copy method implementations from the canonical client-system prototype.
    // ClientProcess.decorate() does the same thing on the production side.
    for (const key of Object.getOwnPropertyNames(CLIENT_SYSTEM_PROTO)) {
      if (key in system) continue;
      const desc = Object.getOwnPropertyDescriptor(CLIENT_SYSTEM_PROTO, key);
      if (desc) Object.defineProperty(system, key, desc);
    }
    Object.defineProperty(system, '__is_client', {
      value: true, writable: false, configurable: false, enumerable: false,
    });
    if (!('requestCbs' in system)) {
      Object.defineProperty(system, 'requestCbs', {
        value: {}, writable: true, configurable: true, enumerable: true,
      });
    }
    if (!('states' in system)) {
      Object.defineProperty(system, 'states', {
        value: {}, writable: true, configurable: true, enumerable: true,
      });
    }
    return system;
  }

  public onAddedSystem(system: System<any>): void {
    if (!this._connector) return; // pre-initialize add: caller will wire later
    // Seed $areaData with the single area the harness emulates (id matches
    // `createAreaServerStub`'s 'test-area-1'). With exactly one entry,
    // `proto.requestArea(...)` auto-resolves `areaId` and routes through
    // `connector.requestServer` → `harness.forwardRequestToServer` without
    // the test having to know the magic area id. See clientSystemMethods.ts
    // for the auto-resolve logic.
    Object.defineProperty(system, '$areaData', {
      get: () => DEFAULT_TEST_AREA_DATA,
      set: () => { throw new Error('Can not set $areaData on system'); },
      configurable: true,
    });
    (system as any).initialize?.(this._client, this._connector);
    (system as any).isNetworked = false;
  }

  protected onStartLoop(): void { this.started = true; }
  protected onStopLoop(): void { this.started = false; }
}

// ── createTestHarness ────────────────────────────────────────────────────────

export function createTestHarness(clientCount = 1): TestHarness {
  // Make per-client window key listeners focus-scoped (idempotent). Combined
  // with harness-core's per-client registering context, a press routes to only
  // the focused client — so generated systems need no isFocused gate.
  installFocusScopedInput();
  const n = Math.max(1, clientCount);
  const serverProc = new BrowserServerProcess();
  // N real split-screen clients sharing the one server — ids match
  // SandboxRuntime/initializeRuntime so a server's `onClientJoined(data.id)` /
  // `onClientMessage(clientId, msg)` sees the same `player-1..player-N` ids the
  // UI's "Add Client" button uses.
  const clientIds = Array.from({ length: n }, (_, i) => `player-${i + 1}`);
  const clientProcs = clientIds.map(() => new TestProcess());

  let harness: TestHarness;

  const areaServerStub = createAreaServerStub(
    () => harness.forwardToClient,
    () => harness.forwardRequestToClient,
  );

  serverProc.initialize(areaServerStub);

  // One stub connector PER client, each tagging its client→server dispatches
  // with that client's id so the shared server can attribute them. `harness`
  // is captured by closure and populated by buildTestHarness below — same
  // trick `areaServerStub` uses.
  //
  // The `send` decoder mirrors what `connector.send([...])` does in
  // production (see packages/client/src/clientSystemMethods.ts):
  //   [CLIENT_TO_SERVER_MESSAGE,                     type, data, to]
  //   [CLIENT_TO_SERVER_MESSAGE_INSTANT,             type, data, to]
  //   [CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE,         type, data, areaId, to]
  //   [CLIENT_TO_DIRECT_AREA_SERVER_MESSAGE_INSTANT, type, data, areaId, to]
  // Position 1 is always `type`, position 2 is always `data`, and the last
  // position is always `to`. We don't depend on the PROTOCOLS enum values
  // so this stays stable if they shift.
  clientProcs.forEach((clientProc, i) => {
    const clientId = clientIds[i];
    clientProc.initialize({
      send: (args: any[]) => {
        if (!Array.isArray(args) || args.length < 4) return;
        const type = args[1];
        const data = args[2];
        const to = args[args.length - 1];
        const areaId = args.length === 5 ? args[3] : undefined;
        harness.forwardToServer({ type, data, to, areaId, clientId });
      },
      sendRaw: () => {},
      requestServer: (msg: any) => harness.forwardRequestToServer(msg),
    });
  });

  harness = buildTestHarness(serverProc, clientProcs, clientIds);

  // Auto-start is LAZY: harness-core wires start() to fire on the first
  // tick() call. That way `beforeEach { addClientSystem ... harness.tick() }`
  // produces the production-style order (add systems → start → tick →
  // onStart fires per system → onUpdate runs) without the test having
  // to remember to call start() explicitly. Earlier eager-start broke
  // exactly this: by start time no systems were added, so onStart never
  // fired for anything.

  return harness;
}

// ── setupSystemPair convenience ──────────────────────────────────────────────

export function setupSystemPair<S, C>(
  opts: { server: S | (new () => S); client: C | (new () => C) },
): { harness: TestHarness; server: S; client: C } {
  return _setupSystemPair(createTestHarness, opts);
}
